import React, { useState, useEffect } from "react";
import { Switch, Loading } from "@nextui-org/react";
import { useSelector } from "react-redux";
import { secureFetch } from "../../lib/api";
import { success, error } from "../../lib/userAlerts";
import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";

const CrossCompanySettings = () => {
  const [state, setState] = useState({
    allowSendMessage: false,
    allowReopenCloseOrder: false,
    allowShareShipmentData: false,
    loading: false,
    loadinge: false,
    addNewCannedMessage: "",
    fetching: true,
    togglingId: null
  });

  const Access = useSelector(state => state.navigation.Access);
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  const getCrossCompanySettings = async () => {
    try {
      const [ok, , json] = await secureFetch(endpoint.fetchCrossCompanySettings, {});
      
      if (ok) {
        if (json.crossCompantSettings.length > 0) {
          setState(prev => ({
            ...prev,
            allowSendMessage: json.crossCompantSettings[0].allow_brokers_to_send_message === 1,
            allowReopenCloseOrder: json.crossCompantSettings[0].allow_brokers_to_reopen_closed_stop === 1,
            allowShareShipmentData: json.crossCompantSettings[0].allow_brokers_to_share_shipment_data === 1,
            loading: false,
            fetching: false
          }));
        } else {
          setState(prev => ({
            ...prev,
            allowSendMessage: false,
            allowReopenCloseOrder: false,
            allowShareShipmentData: false,
            loading: false,
            fetching: false
          }));
        }
      } else {
        setState(prev => ({ ...prev, fetching: false, loading: false }));
        error("Oops! error occurred during fetching cross company settings!");
      }
    } catch (err) {
      setState(prev => ({ ...prev, fetching: false, loading: false }));
      error("Oops! error occurred during fetching cross company settings!");
    }
  };

  const saveAndUpdateCrossCompanySettings = async (
    allowSendMessage,
    allowReopenCloseOrder,
    allowShareShipmentData
  ) => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const [ok, , json] = await secureFetch(endpoint.saveAndUpdateCrossCompanySettings, {
        allowSendMessage: allowSendMessage ? 1 : 0,
        allowReopen: allowReopenCloseOrder ? 1 : 0,
        allowShareShipmentData: allowShareShipmentData ? 1 : 0,
      });

      if (ok) {
        await getCrossCompanySettings();
        success(json.message);
      } else {
        error("Oops! error occurred during save and update cross company settings!");
      }
    } catch (err) {
      error("Oops! error occurred during save and update cross company settings!");
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    getCrossCompanySettings();
  }, [clientId]);

  const handleToggle = async (setting, currentValue) => {
    const newState = {
      allowSendMessage: setting === 'message' ? !currentValue : state.allowSendMessage,
      allowReopenCloseOrder: setting === 'reopen' ? !currentValue : state.allowReopenCloseOrder,
      allowShareShipmentData: setting === 'share' ? !currentValue : state.allowShareShipmentData
    };

    setState(prev => ({ ...prev, togglingId: setting }));
    try {
      await saveAndUpdateCrossCompanySettings(
        newState.allowSendMessage,
        newState.allowReopenCloseOrder,
        newState.allowShareShipmentData
      );
    } finally {
      setState(prev => ({ ...prev, togglingId: null }));
    }
  };

  if (state.fetching) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Loading size="lg" className="text-blue-600 mb-4" />
          <div className="text-gray-600">Fetching cross company settings...</div>
        </div>
      </div>
    );
  }

  if (Access.allow_artifax !== 1) {
    return null;
  }

  return (
    <div className="w-full px-2">
      <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-dotted">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800">
            Cross Company Settings
          </h2>
          <p className="text-gray-600">
            Configure settings for carriers who ship for brokers
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors">
            <div className="flex-1">
              <p className="text-gray-700 font-medium">
                Allow brokers I do business with to send message to my drivers directly
              </p>
            </div>
            <div className="ml-4">
              {state.togglingId === 'message' ? (
                <Loading size="sm" color="primary" />
              ) : (
                <Switch
                  checked={state.allowSendMessage}
                  size="sm"
                  onChange={() => handleToggle('message', state.allowSendMessage)}
                  className="hover:opacity-80 transition-opacity"
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors">
            <div className="flex-1">
              <p className="text-gray-700 font-medium">
                Allow brokers I do business with to reopen closed stop
              </p>
            </div>
            <div className="ml-4">
              {state.togglingId === 'reopen' ? (
                <Loading size="sm" color="primary" />
              ) : (
                <Switch
                  checked={state.allowReopenCloseOrder}
                  size="sm"
                  onChange={() => handleToggle('reopen', state.allowReopenCloseOrder)}
                  className="hover:opacity-80 transition-opacity"
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors">
            <div className="flex-1">
              <p className="text-gray-700 font-medium">
                Allow brokers I do business with to share shipment data
              </p>
            </div>
            <div className="ml-4">
              {state.togglingId === 'share' ? (
                <Loading size="sm" color="primary" />
              ) : (
                <Switch
                  checked={state.allowShareShipmentData}
                  size="sm"
                  onChange={() => handleToggle('share', state.allowShareShipmentData)}
                  className="hover:opacity-80 transition-opacity"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossCompanySettings;